import type React from 'react';
import { type FC, useMemo } from 'react';
import { Button, Col, Container, Row } from 'react-bootstrap';
import Form from 'react-bootstrap/Form';
import Image from 'react-bootstrap/Image';
import Modal from 'react-bootstrap/Modal';

import { FileText } from 'react-bootstrap-icons';
import './styles/styles.css';
import { ErrorMessage } from '@hookform/error-message';
import Carousel from 'react-bootstrap/Carousel';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { PackageParamTypes } from '../../../modules/enums/packages';
import type { PackagesResponse } from '../../../modules/interfaces';

interface UserDetailsModalProps {
  show: boolean;
  handleClose: () => void;
  packageItem: PackagesResponse;
}
interface paramsOptions {
  value: string;
  price: number;
  quantity: number;
  type: string;
}

/* Admins store the standalone QUANTITY param as free text; anything that is not a positive integer means no cap */
const parseMaxQuantity = (value?: string): number | undefined => {
  const parsed = Number.parseInt(value || '', 10);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : undefined;
};

export const CustomizeStay: FC<UserDetailsModalProps> = ({
  show,
  handleClose,
  packageItem,
}) => {
  const {
    control,
    register,
    setValue,
    getValues,
    formState: { errors },
    trigger,
    reset,
  } = useFormContext();

  const [size, price, quantity] = useWatch({
    control,
    name: ['size', 'price', 'quantity'],
  });

  const { onBlur, name, ref } = register('size', {
    required: 'Choose one size or range',
  });

  const paramsSelectedOptions = useMemo<paramsOptions[]>(
    () =>
      packageItem.params?.reduce((accumulator: paramsOptions[], item) => {
        if (
          item.type === PackageParamTypes.RANGE ||
          item.type === PackageParamTypes.SIZE
        ) {
          accumulator.push({
            value: item.values,
            price: item.price || 0,
            quantity: item.quantity || 1,
            type: item.type,
          });
        }
        return accumulator;
      }, []) || [],
    [packageItem],
  );

  const quantityParam = useMemo(
    () => packageItem.params?.find((item) => item.type === PackageParamTypes.QUANTITY),
    [packageItem],
  );

  const hasSizeOrRange = paramsSelectedOptions.length > 0;
  const showQuantity = hasSizeOrRange || !!quantityParam;

  /* Derived from the current selection so it never leaks between add-ons; undefined means no cap */
  const maxQuantity = hasSizeOrRange
    ? paramsSelectedOptions.find((item) => item.value === size)?.quantity || 1
    : parseMaxQuantity(quantityParam?.values);

  const onClickSubmit = async () => {
    let validated = false;
    if (hasSizeOrRange) {
      setValue('amount', getValues('price') * getValues('quantity'));
      validated = await trigger(['price', 'quantity', 'size']);
    } else {
      if (!showQuantity) {
        setValue('quantity', 1);
      }
      setValue('price', packageItem.price || '');
      setValue('amount', (packageItem.price || 0) * getValues('quantity'));
      validated = await trigger(['price', 'quantity']);
    }
    setValue('status', 'PENDING');
    setValue('packageId', packageItem?.uuid || '');

    if (validated) {
      handleClose();
    }
  };

  const changeValue = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = paramsSelectedOptions.find((item) => item?.value === e.target.value);

    setValue('size', e.target.value);
    setValue('price', value?.price);
    await trigger(['price', 'size', 'quantity']);
  };

  const handleCancel = () => {
    reset({
      price: 0,
      quantity: 1,
      reservationId: '',
      packageId: '',
      amount: 0,
      size: '',
    });
    handleClose();
  };

  const unitPrice = packageItem.price || price || 0;
  const total = unitPrice * (Number(quantity) || 0);

  return (
    <Modal
      show={show}
      onHide={handleClose}
      dialogClassName="post-editor-modal"
      backdrop="static"
      id="guest-portal"
    >
      <Modal.Header className="guest-modal-header ">
        <Modal.Title className="editor-title">
          <FileText size={25} /> {'  '}
          {packageItem ? packageItem.name : 'Request Add-On'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="guest-modal-header">
        <Row className="mt-16px">
          {packageItem?.media && packageItem?.media?.length > 0 ? (
            <Carousel>
              {packageItem.media?.map((item) => (
                <Carousel.Item key={item.uri}>
                  <Image src={item?.uri} className="corousselImages" />
                </Carousel.Item>
              ))}
            </Carousel>
          ) : (
            <Image src="/no_image.jpg" className="corousselImages" />
          )}
        </Row>
        {showQuantity && (
          <>
            <Row className="mt-16px">
              {hasSizeOrRange && (
                <Col xs="9">
                  <Form.Label className="editor-title-label">
                    Select {paramsSelectedOptions[0].type.toLocaleLowerCase()}
                  </Form.Label>

                  <Form.Select
                    className="editor-control guest-inputs"
                    placeholder="Select days"
                    value={size || ''}
                    onChange={(e) => changeValue(e)}
                    onBlur={onBlur}
                    name={name}
                    ref={ref}
                  >
                    <option value="">
                      Please select a {paramsSelectedOptions[0].type.toLocaleLowerCase()}
                    </option>
                    {paramsSelectedOptions.map((item) => {
                      return (
                        <option key={item.value} value={item.value}>
                          {item.value}
                        </option>
                      );
                    })}
                  </Form.Select>
                </Col>
              )}
              <Col xs="3">
                <Form.Label className="editor-title-label">Quantity</Form.Label>
                <Controller
                  name="quantity"
                  control={control}
                  rules={{
                    required: 'This is required.',
                    validate: {
                      integer: (value) =>
                        Number.isInteger(value) || 'Please enter a whole number',
                      min: (value) => value >= 1 || 'Please add at least 1',
                      max: (value) =>
                        maxQuantity === undefined ||
                        value <= maxQuantity ||
                        `the max quantity is ${maxQuantity}`,
                    },
                  }}
                  render={({ field }) => (
                    <Form.Control
                      className="editor-control guest-inputs"
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      placeholder="Qty"
                      name={field.name}
                      ref={field.ref}
                      onBlur={field.onBlur}
                      value={field.value ?? ''}
                      onChange={(e) => {
                        const digits = e.target.value.replace(/\D/g, '');
                        field.onChange(digits === '' ? '' : Number(digits));
                      }}
                    />
                  )}
                />
                <ErrorMessage
                  errors={errors}
                  name="quantity"
                  render={({ message }) => <span className="error-text">{message}</span>}
                />
              </Col>
            </Row>

            <ErrorMessage
              errors={errors}
              name="size"
              render={({ message }) => <span className="error-text">{message}</span>}
            />
          </>
        )}
        <Row>
          <h3 className="editor-title mt-16px ">{total.toFixed(2)} USD</h3>

          <Form.Text>
            {packageItem.description ||
              'Did someone say champagne? Let&apos;s start your arrival to The Coach House on a good note - champagne on ice with two glasses on ice'}
          </Form.Text>
        </Row>
      </Modal.Body>

      <Container className="guest-modal-footer ">
        <Button className="cancel-button no-border-radius" onClick={handleCancel}>
          Cancel
        </Button>
        <Button
          className="save-button no-border-radius"
          style={{ marginLeft: '10px' }}
          onClick={onClickSubmit}
        >
          Continue
        </Button>
      </Container>
    </Modal>
  );
};
